import { get, filter } from "lodash";
import { Formik, Form, ErrorMessage } from "formik";
import * as yup from "yup";
import { Button } from "primereact/button";
import { CheckDuplicate } from "components/CheckDuplicate";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { ProgressSpinner } from "primereact/progressspinner";
import { Title } from "components/Title";
import useApp from "hooks/useApp";
import { useTranslation } from "react-i18next";
import useAddPage from "hooks/useAddPage";
import { InputMask } from "primereact/inputmask";
const ActionsAddPage = (props) => {
  const app = useApp();
  const { t } = useTranslation();

  //form validation rules
  const validationSchema = yup.object().shape({
    org_code: yup.string().required(t("FACILITY_REQUIRED")),
    module_id: yup.number().required(t("MODULE_REQUIRED")),
    action_name: yup.string().required(t("NAME_REQUIRED")),
    metrics_id: yup.string().nullable().label("Metrics Id"),
    details: yup.string().label("Description"),
    // sla_time: yup.string().required().label("Sla Time"),
    sla_time: yup
      .string()
      .matches(
        /^([01]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/,
        t("INVALID_TIME_FORMAT")
      )
      .required(t("SLA_TIME_REQUIRED")),
  });

  //form default values
  const formDefaultValues = {
    org_code: "",
    module_id: "",
    action_name: "",
    metrics_id: null,
    details: "",
    sla_time: "",
  };

  //page hook where logics resides
  const pageController = useAddPage({ props, formDefaultValues, afterSubmit });

  // destructure and grab what the page needs
  const {
    formData,
    resetForm,
    handleSubmit,
    submitForm,
    pageReady,
    loading,
    saving,
    inputClassName,
  } = pageController;
  //event raised after form submit
  function afterSubmit(response) {
    app.flashMsg(props.msgTitle, props.msgAfterSave);
    resetForm();
    app.getAllActions();
    if (app.isDialogOpen()) {
      app.closeDialogs(); // if page is open as dialog, close dialog
    } else if (props.redirect) {
      app.navigate(`/actions`);
    }
  }
  const handleReset = (formik) => {
    formik.resetForm();
  };
  const getInputClassName = (error, touched) => {
    return error && touched ? "p-invalid" : "";
  };
  // page loading form data from api
  if (loading) {
    return (
      <div className="p-3 text-center">
        <ProgressSpinner style={{ width: "50px", height: "50px" }} />
      </div>
    );
  }

  //page has loaded any required data and ready to render
  if (pageReady) {
    return (
      <main id="ActionsAddPage" className="main-page">
        {props.showHeader && (
          <section className="page-section mb-3">
            <div className="container">
              <div className="grid align-items-center">
                {!props.isSubPage && (
                  <div className="col-fixed ">
                    <Button
                      onClick={() => app.navigate(-1)}
                      label=""
                      className="p-button p-button-text "
                      icon="pi pi-arrow-left"
                    />
                  </div>
                )}
                <div className="col ">
                  <Title
                    title={t("CREATE_ACTION")}
                    titleClass="text-2xl text-primary font-bold"
                    subTitleClass="text-500"
                    separator={false}
                  />
                </div>
              </div>
            </div>
          </section>
        )}
        <section className="page-section ">
          <div className="container">
            <div className="grid ">
              <div className="md:col-9 sm:col-12 comp-grid">
                <div>
                  <Formik
                    initialValues={formData}
                    validationSchema={validationSchema}
                    onSubmit={(values, actions) => submitForm(values)}
                  >
                    {(formik) => (
                      <>
                        <Form className={`${!props.isSubPage ? "card  " : ""}`}>
                          <div className="grid">
                            <div className="col-12">
                              <div className="formgrid grid">
                                <div className="col-12 md:col-3">
                                  {t("FACILITIES")} *
                                </div>
                                <div className="col-12 md:col-9">
                                  <Dropdown
                                    value={formik.values.org_code || null}
                                    onChange={(e) => {
                                      const newValue =
                                        e.value === undefined ? "" : e.value;
                                      formik.setFieldValue(
                                        "org_code",
                                        newValue
                                      );
                                      formik.setFieldValue("module_id", "");
                                      formik.setFieldTouched("org_code", false);
                                    }}
                                    options={get(props, "organisations", [])}
                                    placeholder={t("SELECT_FACILITY")}
                                    optionLabel="name"
                                    optionValue="code"
                                    className={`${getInputClassName(
                                      formik.errors.org_code,
                                      formik.touched.org_code
                                    )} w-full`}
                                    {...(formik.values.org_code && {
                                      showClear: true,
                                    })}
                                  />
                                  <ErrorMessage
                                    name="org_code"
                                    component="span"
                                    className="p-error"
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="formgrid grid">
                                <div className="col-12 md:col-3">
                                  {t("MODULE")} *
                                </div>
                                <div className="col-12 md:col-9">
                                  <Dropdown
                                    options={filter(
                                      get(props, "modules", []),
                                      (module) =>
                                        module.org_code ===
                                        formik.values.org_code
                                    )}
                                    optionLabel="module_name"
                                    optionValue="id"
                                    placeholder={t("SELECT_MODULE")}
                                    className={`w-full ${getInputClassName(
                                      formik.errors.module_id,
                                      formik.touched.module_id
                                    )}`}
                                    value={formik.values.module_id}
                                    emptyMessage={t("NO_RECORDS_FOUND")}
                                    onChange={(e) =>
                                      formik.setFieldValue("module_id", e.value)
                                    }
                                    showClear={!!formik.values.module_id}
                                  />
                                  <ErrorMessage
                                    name="module_id"
                                    component="span"
                                    className="p-error"
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="formgrid grid">
                                <div className="col-12 md:col-3">
                                  {t("NAME")} *
                                </div>
                                <div className="col-12 md:col-9">
                                  <CheckDuplicate
                                    value={formik.values.org_code}
                                    match={{
                                      key: "action_name",
                                      value: formik.values.action_name,
                                    }}
                                    unMatch={{
                                      key: "id",
                                      value: null,
                                    }}
                                    apiPath="actions/get_by_org"
                                  >
                                    {(checker) => (
                                      <>
                                        <InputText
                                          name="action_name"
                                          onBlur={checker.check}
                                          onChange={formik.handleChange}
                                          value={formik.values.action_name}
                                          label="Name"
                                          type="text"
                                          placeholder={t("ENTER_NAME")}
                                          className={getInputClassName(
                                            formik?.errors?.action_name,
                                            formik.touched.action_name
                                          )}
                                          style={{ width: "100%" }}
                                        />
                                        <ErrorMessage
                                          name="action_name"
                                          component="span"
                                          className="p-error"
                                        />
                                        {!checker.loading && checker.exist && (
                                          <small className="p-error">
                                            {t("ALREADY_EXISTS")}
                                          </small>
                                        )}
                                        {checker.loading && (
                                          <small className="text-500">
                                            {t("CHECKING")}
                                          </small>
                                        )}
                                      </>
                                    )}
                                  </CheckDuplicate>
                                </div>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="formgrid grid">
                                <div className="col-12 md:col-3">
                                  {t("METRICS_ID")}
                                </div>
                                <div className="col-12 md:col-9">
                                  <InputText
                                    name="metrics_id"
                                    onChange={formik.handleChange}
                                    value={formik.values.metrics_id}
                                    label="Metrics Id"
                                    type="text"
                                    placeholder={t("ENTER_METRICS_ID")}
                                    className={inputClassName(
                                      formik?.errors?.metrics_id
                                    )}
                                    disabled
                                  />
                                  <ErrorMessage
                                    name="metrics_id"
                                    component="span"
                                    className="p-error"
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="formgrid grid">
                                <div className="col-12 md:col-3">
                                  {t("DETAILS")}
                                </div>
                                <div className="col-12 md:col-9">
                                  <InputTextarea
                                    name="details"
                                    onChange={formik.handleChange}
                                    value={formik.values.details}
                                    label="Details"
                                    type="text"
                                    placeholder={t("ENTER_DETAILS")}
                                    className={inputClassName(
                                      formik?.errors?.details
                                    )}
                                    rows={5}
                                  />
                                  <ErrorMessage
                                    name="details"
                                    component="span"
                                    className="p-error"
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-12">
                              <div className="formgrid grid">
                                <div className="col-12 md:col-3">
                                  {t("SLA_TIME")} *
                                </div>
                                <div className="col-12 md:col-9">
                                  <InputMask
                                    name="sla_time"
                                    mask="99:99:99"
                                    onChange={formik.handleChange}
                                    value={formik.values.sla_time}
                                    slotChar="00:00:00"
                                    label="Sla Time"
                                    type="text"
                                    placeholder={t("ENTER_TIME")}
                                    className={getInputClassName(
                                      formik?.errors?.sla_time,
                                      formik.touched.sla_time
                                    )}
                                    style={{ width: "100%" }}
                                  />
                                  <ErrorMessage
                                    name="sla_time"
                                    component="span"
                                    className="p-error"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                          {props.showFooter && (
                            <div className="text-center my-3">
                              <Button
                                onClick={(e) => handleSubmit(e, formik)}
                                className="mr-2"
                                type="submit"
                                label={t("SUBMIT")}
                                icon="pi pi-send"
                                loading={saving}
                              />
                              <Button
                                onClick={() => handleReset(formik)}
                                className="p-button-secondary mr-2"
                                type="button"
                                label={t("RESET")}
                                icon="pi pi-refresh"
                              />
                            </div>
                          )}
                        </Form>
                      </>
                    )}
                  </Formik>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    );
  }
};

//page props and default values
const withDefaultProps = (Component) => {
  const WrappedComponent = (props) => {
    const { t } = useTranslation();
    const defaultProps = {
      primaryKey: "id",
      pageName: "actions",
      apiPath: "actions/add_actions",
      routeName: "actionsadd",
      submitButtonLabel: "Submit",
      formValidationError: t("FORM_IS_INVALID"),
      formValidationMsg: t("PLEASE_COMPLETE_THE_FORM"),
      msgTitle: t("CREATE_ACTION"),
      msgAfterSave: t("ACTION_CREATED_SUCCESSFULLY"),
      msgBeforeSave: "",
      showHeader: true,
      showFooter: true,
      redirect: true,
      isSubPage: false,
    };
    return <Component {...defaultProps} {...props} />;
  };

  return WrappedComponent;
};
export default withDefaultProps(ActionsAddPage);
